import { currencyPrecision } from 'app/helpers/money';
import { FormContext, Period } from './useFormContext';

const floorAmount = (v: string | number, currency: string) => {
  const mult = 10 ** currencyPrecision(currency);
  return Math.floor(Number(v) * mult) / mult;
};

// eslint-disable-next-line max-lines-per-function
const useTransactionLimits = (context: FormContext, vendorCurrency: string): [number, number] => {
  const { currencyUsage, fees, dynamicConversionFee, rate } = context;

  const maxPaysystemTransaction = () => {
    const periods: Period[] = ['tnx_max', 'daily', 'weekly', 'monthly'];
    const candidates = periods
      .filter((period) => +currencyUsage.limits[period] !== 0)
      .map((period) => {
        const diff = +currencyUsage.limits[period] - +currencyUsage.usage[period];
        return diff >= 0 ? diff : 0;
      });

    return Math.min(...candidates); // returns Infinity in case of no arguments
  };

  const maxPlayerTransaction = () => {
    const stat = context.playerPaymentLimitUsage;

    if (!stat) return Infinity;

    const diff = +stat.limit - +stat.usage;
    if (diff < 0) return 0;

    // Выясняем, какую максимальную сумму можно вбить, чтобы после вычета комиссий остаться в рамках лимитов.
    // То есть находим x для известного y в функции
    // y = x * (1 - conversion_fee) - max(x * operation_fee, min_fee) - fixed_fee.
    // График функции - возрастающая прямая с переломом
    // (возрастание сохраняется https://www.desmos.com/calculator/wpln3ogktu).
    // Таким образом нужно решать 2 простых уравнения, но сначала нужно понять Y находится выше или ниже точки перелома
    // this.fees.min / this.fees.operation. Поэтому определяем точку перелома, а далее все просто.

    // Фиксированные комиссии здесь добавляются к той сумме, которую игрок вводил в форму.
    // Для выводов это сумма в валюте счёта игрока – комиссия playerFixedFee,
    // для депозитов – vendorFixedFee в валюте операции.
    const playerFixedFee = context.isDeposit ? 0 : fees.fixed;
    const vendorFixedFee = context.isDeposit ? fees.fixed : 0;

    let max = diff + playerFixedFee;
    const breakingPoint = fees.min / fees.operation;
    max = max + fees.min < breakingPoint ? max + fees.min : max / (1 - fees.operation);

    const converted = floorAmount(max / rate / (1 - dynamicConversionFee) + vendorFixedFee, vendorCurrency);
    return converted;
  };

  const minTnx = +currencyUsage.limits.tnx_min;

  const maxTnx = Math.min(maxPlayerTransaction(), maxPaysystemTransaction());

  return [minTnx, maxTnx];
};

export default useTransactionLimits;
